import { useEffect, useRef, useState } from "react";

import {
  FOCUS_AREAS,
  MAX,
  MIN_MESSAGE,
  type EnquiryResponse,
  type FieldErrors,
} from "@/lib/enquiry";

/**
 * The intake form, which works with JavaScript off.
 *
 * ── How that is arranged ───────────────────────────────────────────────────
 *
 * The markup is an ordinary `method="POST"` form pointing at a real endpoint,
 * with `required`, `type`, `minlength` and `maxlength` on the inputs. Load
 * this page with scripting disabled and it submits, the browser having
 * already refused anything obviously wrong, and the Worker redirects to a
 * prerendered confirmation page.
 *
 * With scripting on, an effect sets `noValidate` on the form *element* and
 * takes over `submit`. That is the whole trick, and it has to happen at
 * runtime rather than as a `noValidate` prop in the markup: the attribute has
 * to be absent in the served HTML for the no-JS path to get native
 * validation, and present once we are ready to show our own styled errors
 * instead of the browser's bubbles.
 *
 * ── What this replaced ─────────────────────────────────────────────────────
 *
 * A Next server action driven by `useActionState`. Two notes survive that:
 *
 * React reset the uncontrolled form as soon as the action resolved, which
 * emptied every field on a rejected submit while the error text claimed the
 * message was still there. That needed the server to echo the draft back so
 * the inputs could re-seed from it. None of that applies now — this owns its
 * own submit and never resets — so the draft simply stays put, and the
 * echoing machinery is gone rather than ported.
 *
 * `useFormStatus` needed the submit button to be its own component to read
 * status from inside the form. It does not any more; the button is inline
 * and reads `state` directly.
 */

type State =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "sent" }
  | { status: "invalid"; fieldErrors: FieldErrors }
  | { status: "error"; message: string };

export function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    // Only now that we can render errors ourselves.
    if (formRef.current !== null) formRef.current.noValidate = true;
  }, []);

  if (state.status === "sent") return <Sent />;

  const fieldErrors = state.status === "invalid" ? state.fieldErrors : {};
  const sending = state.status === "sending";

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (sending) return;

    setState({ status: "sending" });

    try {
      const response = await fetch(form.action, {
        method: "POST",
        // The header the Worker branches on: JSON for us, a redirect for a
        // browser posting the form itself.
        headers: { Accept: "application/json" },
        body: new FormData(form),
      });

      const result = (await response.json()) as EnquiryResponse;
      setState(result);
    } catch {
      /*
       * A network failure, not a rejection — the request never arrived, so
       * saying anything about what the server thought would be a guess.
       */
      setState({
        status: "error",
        message:
          "That did not reach us — the connection dropped. Your message is still in the fields above, so try again in a moment.",
      });
    }
  }

  return (
    <form
      className="k-form"
      ref={formRef}
      method="POST"
      action="/api/enquiry"
      onSubmit={onSubmit}
    >
      {/*
        Announced rather than merely displayed: a failure that only exists as
        red text is invisible to somebody who submitted the form by keyboard
        and is not looking at that part of the page.
      */}
      {state.status === "error" && (
        <p className="k-alert" role="alert">
          {state.message}
        </p>
      )}

      <Field
        name="name"
        label="Your name"
        autoComplete="name"
        required
        maxLength={MAX.name}
        error={fieldErrors.name}
      />
      <Field
        name="email"
        label="Email"
        type="email"
        autoComplete="email"
        required
        maxLength={MAX.email}
        error={fieldErrors.email}
      />
      <Field
        name="company"
        label="Company or site"
        optional
        autoComplete="organization"
        placeholder="example.com"
        maxLength={MAX.company}
        error={fieldErrors.company}
      />

      <fieldset className="k-fieldset">
        <legend className="k-field__label">
          What should we look at? <span className="k-field__opt">Optional</span>
        </legend>
        <div className="k-checks">
          {FOCUS_AREAS.map((area) => (
            <label key={area} className="k-checkbox">
              <input type="checkbox" name="focus" value={area} />
              <span>{area}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="k-field">
        <label className="k-field__label" htmlFor="message">
          What is at risk?
        </label>
        <textarea
          className="k-input k-textarea"
          id="message"
          name="message"
          rows={5}
          required
          minLength={MIN_MESSAGE}
          maxLength={MAX.message}
          placeholder="What the system does, who depends on it, and what worries you about it."
          aria-invalid={fieldErrors.message ? true : undefined}
          aria-describedby={fieldErrors.message ? "message-error" : undefined}
        />
        {fieldErrors.message && (
          <p className="k-field__error" id="message-error">
            {fieldErrors.message}
          </p>
        )}
      </div>

      {/*
        The honeypot. Hidden from sight and from the accessibility tree, and
        skipped by the Tab key, so no person can fill it in by accident —
        `display: none` alone is a field some autofill tools still populate.
      */}
      <div className="k-hp" aria-hidden="true">
        <label htmlFor="website">Do not fill this in</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      <div className="k-form__foot">
        <button className="k-btn" type="submit" disabled={sending}>
          {sending ? "Sending…" : "Request a review"}
        </button>
        <p className="k-note">We reply within one working day.</p>
      </div>
    </form>
  );
}

function Sent() {
  return (
    <div className="k-sent" role="status">
      <span className="k-chip k-chip--green">Received</span>
      <h2>Thanks — that is logged.</h2>
      <p>
        We read every enquiry ourselves and reply within one working day,
        usually with a question or two before we start the clock.
      </p>
    </div>
  );
}

type FieldProps = {
  name: string;
  label: string;
  type?: string;
  optional?: boolean;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  maxLength?: number;
  error?: string;
};

function Field({
  name,
  label,
  type = "text",
  optional = false,
  placeholder,
  autoComplete,
  required,
  maxLength,
  error,
}: FieldProps) {
  return (
    <div className="k-field">
      <label className="k-field__label" htmlFor={name}>
        {label}
        {optional && <span className="k-field__opt">Optional</span>}
      </label>
      <input
        className="k-input"
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {error && (
        <p className="k-field__error" id={`${name}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
